/**
 * licenseAuthority.ts (InstaLOD GmbH)
 *
 * Copyright © 2026 InstaLOD GmbH - All Rights Reserved.
 *
 * @file licenseAuthority.ts
 * @section License
 */

/**
 * Which store drives a license **now** — as opposed to how it came to exist.
 *
 * Deliberately separate from {@link LicenseOrigin}, because the two answer different questions and
 * the migration makes them disagree. `origin` is a historical fact: this license was issued by the
 * ECommerce fulfilment path, or by hand, or as a trial, and that never stops being true. `authority`
 * is who is entitled to change it from here on.
 *
 * A migrated license is exactly where they part company. The Abstract Store adopts the license the
 * old store issued rather than minting a second one — so `origin` stays `transaction`, which is what
 * actually happened, while `authority` becomes `store`, which is who renews and revokes it from now
 * on. Folding both into `origin` would mean the adoption rewriting history: a license would claim the
 * new store created it, and nothing would record that a customer's access predates the migration.
 */
export enum LicenseAuthority {
  /**
   * Nobody's store. A pioneer license, a trial, or one an administrator made by hand.
   *
   * The default, and the right default: a license nothing automated owns is one only a person
   * changes, and treating an unknown as store-driven would hand it to a projection that would then
   * revoke it for having no entitlement behind it.
   */
  None = 'none',

  /**
   * The legacy ECommerce store — "Classic".
   *
   * Its fulfilment is keyed on `transactionID`, and its renewal job is what extends the end date. A
   * license left here after the legacy jobs are switched off is renewed by nothing, and expires at
   * whatever date it happens to carry.
   */
  Classic = 'classic',

  /**
   * The Abstract Store, through the entitlement replica and `StoreLicenseBridgeService`.
   *
   * Correlated on `(userUUID, storeEntitlementKey)` and the purchase reference. This is what the
   * bridge's candidate query filters on: a license it does not have authority over is one it must
   * never write to.
   */
  Store = 'store'
}

/**
 * How an authority is named on screen: "Classic" for the legacy store, "Store" for the new one.
 *
 * The names are product names rather than prose, so they are not translated — an operator reading a
 * German screen still has to recognise which of the two systems a support ticket is about, and
 * "Klassisch" is not a name anything else in either store uses. `None` renders an em dash rather than
 * a word: it is the absence of an owner, and spelling it "None" in a column of two real systems reads
 * as a third one.
 *
 * This is the one reader, so a screen cannot label `classic` differently from the next.
 */
export function licenseAuthorityLabel(
  authority: LicenseAuthority | string | null | undefined
): string {
  switch (authority) {
    case LicenseAuthority.Classic:
      return 'Classic';
    case LicenseAuthority.Store:
      return 'Store';
    default:
      return '—';
  }
}
